// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ReviewPreflight.test.tsx
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
} from "@phosphor-icons/react";
import { Checkbox, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { QuestionFormCard } from "./QuestionFormCard";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type ReviewPreflightCategory = {
  id: string;
  label: string;
  description?: string;
  /** Nested under a parent row (Figma scope: Nutrition / Product / Region). */
  indent?: boolean;
};

export type ReviewPreflightProps = {
  variant?: "confirm" | "scope";
  categories: ReviewPreflightCategory[];
  selectedCategoryIds?: string[];
  onToggleCategory?: (id: string) => void;
  onStart: () => void;
  onChangeScope: () => void;
  onCancel?: () => void;
  summary?: string;
  description?: string;
  focusValue?: string;
  onFocusChange?: (value: string) => void;
  testIdPrefix?: string;
};

const PANEL_CLASS =
  "flex flex-col gap-2 rounded-xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 p-2";

const HEADER_CLASS = "flex w-full items-center justify-between";

/**
 * Pre-review confirm / scope picker. AgentSteps-like muted panel + nested
 * QuestionForm inner (Figma ReviewPreflight 49613:704).
 */
export function ReviewPreflight({
  variant = "confirm",
  categories,
  selectedCategoryIds = [],
  onToggleCategory,
  onStart,
  onChangeScope,
  onCancel,
  summary,
  description,
  focusValue,
  onFocusChange,
  testIdPrefix = "review-preflight",
}: ReviewPreflightProps) {
  const { t } = useTranslation("ui");
  const selected = categories.filter((c) => selectedCategoryIds.includes(c.id));
  const confirmTitle =
    summary ??
    (selected.map((c) => c.label).join(", ") || t("reviewPreflight.title"));
  const confirmDescription =
    description ?? t("reviewPreflight.confirmDescription");
  const isScope = variant === "scope";

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewPreflight)}
      data-testid={testIdPrefix}
      className={PANEL_CLASS}
    >
      <div className={HEADER_CLASS}>
        <div className="flex items-center gap-2.5">
          <CircleNotch
            size={16}
            className="shrink-0 text-puntt-accent-9"
            aria-hidden
          />
          <Text size="1" className="text-puntt-neutral-gray-11">
            {t("reviewPreflight.title")}
          </Text>
        </div>
        <CaretDown
          size={16}
          aria-hidden
          className="shrink-0 text-puntt-neutral-gray-9"
        />
      </div>

      <div
        data-testid={`${testIdPrefix}-loaded`}
        className="flex items-center justify-between rounded-xl border border-puntt-neutral-gray-4 bg-base-white/80 p-2"
      >
        <div className="flex items-center gap-2.5">
          <CheckCircle
            size={16}
            className="shrink-0 text-malachite-700"
            aria-hidden
          />
          <Text size="1" className="text-puntt-neutral-gray-11">
            {t("reviewPreflight.loaded")}
          </Text>
        </div>
        <CaretRight
          size={16}
          aria-hidden
          className="shrink-0 text-puntt-neutral-gray-9"
        />
      </div>

      <QuestionFormCard
        chrome="inner"
        testId={`${testIdPrefix}-form`}
        testIdPrefix={testIdPrefix}
        detail1={isScope ? t("reviewPreflight.scopeTitle") : confirmTitle}
        detail2={
          isScope ? t("reviewPreflight.scopeDescription") : confirmDescription
        }
        primary={{
          action: "start",
          testId: `${testIdPrefix}-start`,
          label: t("reviewPreflight.startReview"),
          onClick: onStart,
        }}
        tertiary={
          isScope
            ? onCancel
              ? {
                  action: "cancel",
                  testId: `${testIdPrefix}-cancel`,
                  label: t("reviewPreflight.cancel"),
                  onClick: onCancel,
                }
              : undefined
            : {
                action: "change-scope",
                testId: `${testIdPrefix}-change-scope`,
                label: t("reviewPreflight.changeScope"),
                onClick: onChangeScope,
              }
        }
      >
        {isScope ? (
          <div className="flex flex-col gap-2">
            {categories.map((category) => {
              const checked = selectedCategoryIds.includes(category.id);
              return (
                <label
                  key={category.id}
                  data-testid={`${testIdPrefix}-scope-${category.id}`}
                  className={
                    category.indent
                      ? "flex cursor-pointer items-start gap-2 pl-6"
                      : "flex cursor-pointer items-start gap-2"
                  }
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => onToggleCategory?.(category.id)}
                    aria-label={category.label}
                    size="2"
                  />
                  <span className="flex min-w-0 flex-col">
                    <Text size="2" className="text-puntt-neutral-gray-12">
                      {category.label}
                    </Text>
                    {category.description ? (
                      <Text size="1" className="text-puntt-neutral-gray-11">
                        {category.description}
                      </Text>
                    ) : null}
                  </span>
                </label>
              );
            })}
            {onFocusChange ? (
              <div className="flex flex-col gap-2">
                <Text size="1" className="text-puntt-neutral-gray-11">
                  {t("reviewPreflight.focusLabel")}
                </Text>
                <input
                  data-testid={`${testIdPrefix}-focus`}
                  aria-label={t("reviewPreflight.focusLabel")}
                  value={focusValue ?? ""}
                  onChange={(e) => onFocusChange(e.target.value)}
                  className="h-9 w-full rounded-lg border border-puntt-neutral-gray-4 bg-base-white px-2 text-[14px] text-puntt-neutral-gray-12 outline-none focus:border-puntt-accent-8"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </QuestionFormCard>
    </div>
  );
}
